import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import moment from 'moment'

import RouteLayout from '../../RouteLayout'
import Spinner from '../../common/Spinner'
import { AddEditModal, AddMemberModal } from './Components'
import {
  commissionGetActions,
  commissionMembersActions,
  commissionPeoplesActions,
  commissionMemberDeleteActions,
} from '../../../redux/reducers/Commission/commissionReducer'
import styles from './Commission.module.css'
import { COMMISSION_PLACES_EXPERT, COMMISSION_PLACES_GUK } from '../../../constants/const'
import {PlusIcon, TrashIcon, EditIcon} from '../../../assets/svg/commission'
import ConfirmModal from '../../common/Confirmation'


const FIELDS = [
  { key: 'date_from',   label: 'Дата начала',            format: v => v ? moment(v).format('DD.MM.YYYY') : ''},
  { key: 'date_to',     label: 'Дата окончания',         format: v => v ? moment(v).format('DD.MM.YYYY') : ''},
  { key: 'date_to_guk', label: 'Дата окончания (ГУК)',   format: v => v ? moment(v).format('DD.MM.YYYY') : ''},
  { key: 'created_at',  label: 'Дата создания',          format: v => v},
  { key: 'phone',       label: 'Телефон',               format: v => v || '—' },
  { key: 'closed',      label: 'Не отображать',               format: v => v === '1' ? 'Да' : 'Нет' },
]

const PLACE = {
  expert: COMMISSION_PLACES_EXPERT,
  guk: COMMISSION_PLACES_GUK
}

const Current = () => {

  const { id } = useParams()
  const dispatch = useDispatch()
  const {data, members} = useSelector(state => state.commission.current)
  const loader = useSelector(state => state.commission.loader)

  const [modalOpen, setModalOpen] = useState(false)

  useEffect(() => {
    dispatch(commissionGetActions.commissionGetRequestStart({ id }))
    dispatch(commissionMembersActions.commissionMembersRequestStart({ id }))
    dispatch(commissionPeoplesActions.commissionPeoplesRequestStart())
  }, [dispatch, id])

  return (
    <RouteLayout
      title={'Комиссия'}
      titleExtra={
        data && (
          <button
            className={styles.editTitleBtn}
            onClick={() => setModalOpen(true)}
            title="Редактировать"
          >
            <EditIcon />
          </button>
        )
      }
    >
      {loader || !data
        ? <Spinner />
        : (
          <>
            <div className={styles.infoBlock}>
              {FIELDS.map(({ key, label, format }) => (
                <div key={key} className={styles.infoRow}>
                  <span className={styles.infoLabel}>{label}</span>
                  <span className={styles.infoValue}>{format(data[key])}</span>
                </div>
              ))}
            </div>

            <div className={styles.membersBlock}>
              <MembersList
                data={members}
                mode='expert'
                commissionId={id}
              />
              <MembersList
                data={members}
                mode='guk'
                commissionId={id}
              />
            </div>

            <AddEditModal
              isOpen={modalOpen}
              onClose={() => setModalOpen(false)}
              isEdit
              data={data}
            />
          </>
        )
      }
    </RouteLayout>
  )
}




const MembersList = ({ data = [], mode, commissionId }) => {

  const dispatch = useDispatch()
  const isGuk = mode === 'guk'
  const members = useMemo(() => data.filter(member => member.member_type === mode), [data, mode])
  const title = isGuk ? 'ГУК' : 'Эксперты'

  const [modalOpen, setModalOpen] = useState(false)
  const [editModalOpen, setEditModalOpen] = useState(false)
  const [selectedMid, setSelectedMid] = useState(null)
  const [confirmOpen, setConfirmOpen] = useState(false)

  const PLACES = useMemo(() => PLACE[mode].reduce((acc, i) => ({...acc, [i.value]: i.name}), {}), [mode])

  const selectedMember = useMemo(
    () => members.find(m => m.mid === selectedMid),
    [members, selectedMid],
  )

  const handleSelect = mid => {
    setSelectedMid(prev => prev === mid ? null : mid)
  }

  const handleDeleteClick = () => {
    if (!selectedMid) return
    setConfirmOpen(true)
  }

  const handleDeleteConfirm = () => {
    dispatch(commissionMemberDeleteActions.commissionMemberDeleteRequestStart({ id: selectedMember.id }))
    setConfirmOpen(false)
    setSelectedMid(null)
  }

  const renderMember = m => {
    const name = `${m.LastName} ${m.FirstName} ${m.Patronymic} (${PLACES[m.place]})`
    const isSelected = m.mid === selectedMid
    const itemClass = isSelected
      ? `${styles.memberItem} ${styles.memberItemSelected}`
      : styles.memberItem
    return (
      <li key={m.mid} className={itemClass} onClick={() => handleSelect(m.mid)}>
        {name}
      </li>
    )
  }

  const editBtnClass = selectedMid
    ? styles.memberActionBtn
    : `${styles.memberActionBtn} ${styles.memberActionBtnDisabled}`

  const deleteBtnClass = selectedMid
    ? `${styles.memberActionBtn} ${styles.memberActionBtnDanger}`
    : `${styles.memberActionBtn} ${styles.memberActionBtnDanger} ${styles.memberActionBtnDisabled}`

  return (
    <div className={styles.memberPanel}>
      <AddMemberModal
        type={mode}
        isOpen={modalOpen}
        closeModal={() => setModalOpen(false)}
      />
      <AddMemberModal
        type={mode}
        isOpen={editModalOpen}
        closeModal={() => setEditModalOpen(false)}
        editData={selectedMember}
      />
      <ConfirmModal
        isOpen={confirmOpen}
        title="Удаление участника"
        text={selectedMember
          ? `Удалить ${selectedMember.LastName} ${selectedMember.FirstName} ${selectedMember.Patronymic} из комиссии?`
          : ''
        }
        onConfirm={handleDeleteConfirm}
        onCancel={() => setConfirmOpen(false)}
      />
      <div className={styles.memberPanelHeader}>{title}</div>
      <ul className={styles.memberList}>
        {members.map(renderMember)}
      </ul>
      <div className={styles.memberActions}>
        <button onClick={() => setModalOpen(true)} className={styles.memberActionBtn} title="Добавить">
          <PlusIcon/>
        </button>
        <button
          className={editBtnClass}
          title="Редактировать"
          onClick={() => setEditModalOpen(true)}
          disabled={!selectedMid}
        >
          <EditIcon />
        </button>
        <button
          className={deleteBtnClass}
          title="Удалить"
          onClick={handleDeleteClick}
          disabled={!selectedMid}
        >
          <TrashIcon />
        </button>
      </div>
    </div>
  )
}

export default Current
